import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { primeServices } from "@services/apis/Prime";
import {
  GetPrimeTradesListRequest,
  GetPrimeTradesListResponse,
  PrimeTradeData,
} from "@services/models/Prime/GetPrimeTradeListModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { RowClickedEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import ConstantFunctions from "../../utils/constants_functions";
import { CSV_EXPORT_CONSTANTS } from "../../utils/csvExportConstants";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import ConfirmExpireModal from "./components/ConfirmExpireModal";
import { createPrimeOrderColumnDefs } from "./components/PrimeOrderColumnDefs";
import PrimeDetailsTabs from "./PrimeDetailsTabs";
import { getPrimeOrderAgRowClass } from "./utils/primeOrderRowClass";

interface PrimeOrderFlowProps {
  countryFilter?: "IN" | "BR";
}

const PrimeOrderFlow = (props: PrimeOrderFlowProps) => {
  const countryFilter: "IN" | "BR" = props.countryFilter ?? "IN";
  const { handleSuccess, handleError } = useSnackbarMessage();
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<any>(null);
  const ROWS_PER_PAGE = 1000;

  const [confirmTradeId, setConfirmTradeId] = useState<number | null>(null);
  const [confirmReason, setConfirmReason] = useState<"EXPIRED" | "COMPLETED">(
    "EXPIRED",
  );
  const [orders, setOrders] = useState<PrimeTradeData[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [tradeId, setTradeId] = useState<string | undefined>(undefined);
  const [tradeType, setTradeType] = useState<string[] | undefined>(undefined);
  const [tradeStatus, setTradeStatus] = useState<string[] | undefined>(
    undefined,
  );
  const [isAutoSettle, setIsAutoSettle] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);
  const [expiringTradeIds, setExpiringTradeIds] = useState<Set<number>>(
    new Set(),
  );

  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);

  const fetchPrimeOrders = async (pageNum: number, reset: boolean = false) => {
    if (inFlight.current) return;
    inFlight.current = true;

    const data: GetPrimeTradesListRequest = {
      tradeType: tradeType ? tradeType : undefined,
      tradeStatus: tradeStatus ? tradeStatus : undefined,
      page: pageNum,
      rows: ROWS_PER_PAGE,
      primeType: countryFilter,
      isAutoSettle: isAutoSettle ? 1 : undefined,
    };
    try {
      setIsLoading(true);
      const response: GetPrimeTradesListResponse =
        await primeServices.getPrimeTradesList(data);
      if (response.data.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      if (reset) {
        setOrders(response.data);
      } else {
        setOrders((prevOrders) => [...prevOrders, ...response.data]);
      }
    } catch (error: any) {
      setError(error.message || "Failed to fetch prime orders.");
    } finally {
      setIsLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (!didInitialFetch.current) {
      didInitialFetch.current = true;
      setPage(1);
      setHasMore(true);
      fetchPrimeOrders(1, true);
    }
  }, []);

  useEffect(() => {
    if (!didInitialFetch.current) return;
    setPage(1);
    setHasMore(true);
    fetchPrimeOrders(1, true);
  }, [tradeType, tradeStatus, isAutoSettle]);

  useEffect(() => {
    if (page > 1 && hasMore && !searchTerm) {
      fetchPrimeOrders(page, false);
    }
  }, [page, hasMore, searchTerm]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || isLoading || searchTerm) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading, searchTerm],
  );

  const onRowClicked = (event: RowClickedEvent) => {
    const target = event.event?.target as HTMLElement;

    if (target?.closest("[data-ignore-row-click]")) {
      return;
    }
    const selectedData = event.data as PrimeTradeData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setTradeId(selectedData.tradeId.toString());
    setIsPanelCollapsed(false);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (gridRef.current && orders.length > 0 && !searchTerm) {
        gridRef.current.api.refreshCells({
          columns: ["active"],
          force: true,
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [orders.length, searchTerm]);

  useEffect(() => {
    const jwt = secureLocalStorage.getItem("jwt");
    webSocketService.connect({ token: jwt }, true);
  }, []);

  const handleTradeChangeWebSockets = (socketData: PrimeTradeData) => {
    // Early return if search is active or country doesn't match
    if (searchTerm || socketData.countryCode !== countryFilter) {
      return;
    }

    // Check trade type filter
    if (tradeType && tradeType.length > 0) {
      const socketTradeType = socketData.tradeType === "B" ? "Buy" : "Sell";
      if (!tradeType.includes(socketTradeType)) {
        return;
      }
    }

    // Check trade status filter
    if (tradeStatus && tradeStatus.length > 0) {
      let currentStatus = "Pending";
      if (socketData.eventDetails && socketData.eventDetails.length > 0) {
        const sortedEvents = [...socketData.eventDetails].sort((a, b) => {
          return (
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
        });

        const latestEvent = sortedEvents[0];
        switch (String(latestEvent.eventId)) {
          case "1":
            currentStatus = "Received";
            break;
          case "2":
            currentStatus = "Accepted";
            break;
          case "3":
            currentStatus = "Confirmed";
            break;
          case "4":
          case "6":
            currentStatus = "Completed";
            break;
          case "5":
            currentStatus = "Disputed";
            break;
          case "9":
            currentStatus = "Expired";
            break;
          default:
            currentStatus = "Pending";
        }
      }

      if (!tradeStatus.includes(currentStatus)) {
        return;
      }
    }

    setOrders((prevOrders) => {
      const existingTradeIndex = prevOrders.findIndex(
        (order) => String(order.tradeId) === String(socketData.tradeId),
      );

      if (existingTradeIndex !== -1) {
        const previous = prevOrders[existingTradeIndex];
        const updatedOrders = [...prevOrders];
        updatedOrders[existingTradeIndex] = {
          ...previous,
          ...socketData,
          tradeId: Number(socketData.tradeId),
          eventDetails: socketData.eventDetails?.length
            ? socketData.eventDetails
            : previous.eventDetails,
        };
        return updatedOrders;
      } else {
        if (prevOrders.length > 0) {
          const socketTradeId = Number(socketData.tradeId);
          const newestTradeId = Number(prevOrders[0].tradeId);

          if (socketTradeId > newestTradeId) {
            return [socketData, ...prevOrders];
          } else {
            return prevOrders;
          }
        }

        return [socketData, ...prevOrders];
      }
    });
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.protectedSocket?.off("admin_trade_update");

        if (!searchTerm) {
          webSocketService.onMessage(
            "admin_trade_update",
            true,
            handleTradeChangeWebSockets,
          );
        }
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };
    initializeWebSocket();
  }, [
    webSocketService.protectedSocket?.connected,
    searchTerm,
    countryFilter,
    tradeType,
    tradeStatus,
    isAutoSettle,
  ]);

  const handleSearchSubmit = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimeTradesListResponse =
        await primeServices.searchPrimeTrade(searchTerm, countryFilter);
      setOrders(response.data);
      setHasMore(false);
    } catch (error: any) {
      setError(error.message || "Failed to fetch prime orders.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
    setHasMore(true);
    fetchPrimeOrders(1, true);
  };

  const exportToCsv = async () => {
    try {
      setExportLoading(true);
      if (countryFilter === "BR") {
        if (!gridRef.current?.api) {
          throw new Error("Grid API not ready");
        }

        const EVENT_IDS = {
          RECEIVED: "1",
          ACCEPTED: "2",
          CONFIRMED: "3",
          COMPLETED: "4",
          COMPLETED_ALT: "6",
          EXPIRED: "9",
          KYC: "10",
        } as const;

        const hasEvent = (data: PrimeTradeData, eventId: string): boolean => {
          return (
            data.eventDetails?.some((e: any) => e.eventId === eventId) ?? false
          );
        };

        const getEventTimeDiff = (
          data: PrimeTradeData,
          eventId: string,
        ): string => {
          if (!data.eventDetails || data.eventDetails.length === 0)
            return "00:00";

          const sortedEvents = [...data.eventDetails].sort((a: any, b: any) => {
            return (
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
          });

          const eventIndex = sortedEvents.findIndex(
            (e: any) => e.eventId === eventId,
          );
          if (eventIndex === -1) return "";

          if (eventIndex === 0) {
            const eventTime = new Date(
              sortedEvents[eventIndex].timestamp,
            ).getTime();
            const creationTime = new Date(data.createdAt).getTime();
            const diffMs = eventTime - creationTime;
            const minutes = Math.floor(diffMs / 60000);
            const seconds = Math.floor((diffMs % 60000) / 1000);
            return `${minutes.toString().padStart(2, "0")}:${seconds
              .toString()
              .padStart(2, "0")}`;
          }

          const currentEventTime = new Date(
            sortedEvents[eventIndex].timestamp,
          ).getTime();
          const prevEventTime = new Date(
            sortedEvents[eventIndex - 1].timestamp,
          ).getTime();
          const diffMs = currentEventTime - prevEventTime;
          const minutes = Math.floor(diffMs / 60000);
          const seconds = Math.floor((diffMs % 60000) / 1000);

          return `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`;
        };

        const getActiveTime = (data: PrimeTradeData): string => {
          const updateTimeFromCreated = (): string => {
            const createdAtTime = new Date(data.createdAt).getTime();
            const now = Date.now();
            const diffMs = now - createdAtTime;
            const minutes = Math.floor(diffMs / 60000);
            const seconds = Math.floor((diffMs % 60000) / 1000);
            return `${minutes.toString().padStart(2, "0")}:${seconds
              .toString()
              .padStart(2, "0")}`;
          };

          const sortedEventDetails = [...(data.eventDetails || [])].sort(
            (a: any, b: any) => {
              return (
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
              );
            },
          );

          const latestEvent = sortedEventDetails[0];
          if (latestEvent && ["9", "6", "4"].includes(latestEvent.eventId)) {
            const eventTime = new Date(latestEvent.timestamp).getTime();
            const creationTime = new Date(data.createdAt).getTime();
            const diffMs = eventTime - creationTime;
            const minutes = Math.floor(diffMs / 60000);
            const seconds = Math.floor((diffMs % 60000) / 1000);
            return `${minutes.toString().padStart(2, "0")}:${seconds
              .toString()
              .padStart(2, "0")}`;
          }

          return updateTimeFromCreated();
        };

        const date = new Date();
        const formattedDate = `${date.getFullYear()}-${String(
          date.getMonth() + 1,
        ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        gridRef.current.api.exportDataAsCsv({
          fileName: `prime-orders-brl-${formattedDate}.csv`,
          skipHeader: false,
          skipColumnGroupHeaders: false,
          skipColumnHeaders: false,
          // Explicit set: avoids exporting UI-only columns like "Expire"
          columnKeys: [
            "createdAt",
            "tradeId",
            "merchantName",
            "isPixAutoSettle",
            "amount",
            "kyc",
            "received",
            "accepted",
            "confirmed",
            "completed",
            "expired",
            "active",
          ],
          onlySelected: false,
          suppressQuotes: false,
          columnSeparator: ",",
          processCellCallback: (params: any) => {
            const data = params?.node?.data as PrimeTradeData | undefined;
            if (!data) return "";

            const colId: string =
              params?.column?.getColId?.() ||
              params?.column?.getColDef?.()?.field ||
              "";

            switch (colId) {
              case "createdAt":
                return constantFunctions.dateFormatInDDMMYY(data.createdAt);
              case "tradeId":
                return String(data.tradeId ?? "");
              case "merchantName":
                return data.merchantName ?? "";
              case "isPixAutoSettle":
                return data.isPixAutoSettle === "1" ? "YES" : "NO";
              case "amount": {
                const crypto =
                  data.cryptoAmount != null ? data.cryptoAmount.toFixed(2) : "";
                const fiat =
                  data.fiatAmount != null ? data.fiatAmount.toFixed(2) : "";
                return [crypto, fiat].filter(Boolean).join(" | ");
              }
              case "kyc":
                return hasEvent(data, EVENT_IDS.KYC) ? "DONE" : "PENDING";
              case "received":
                return hasEvent(data, EVENT_IDS.RECEIVED) ? "YES" : "NO";
              case "accepted":
                return hasEvent(data, EVENT_IDS.ACCEPTED)
                  ? getEventTimeDiff(data, EVENT_IDS.ACCEPTED) || "YES"
                  : "NO";
              case "confirmed":
                return hasEvent(data, EVENT_IDS.CONFIRMED)
                  ? getEventTimeDiff(data, EVENT_IDS.CONFIRMED) || "YES"
                  : "NO";
              case "completed":
                return hasEvent(data, EVENT_IDS.COMPLETED) ||
                  hasEvent(data, EVENT_IDS.COMPLETED_ALT)
                  ? getEventTimeDiff(
                      data,
                      hasEvent(data, EVENT_IDS.COMPLETED)
                        ? EVENT_IDS.COMPLETED
                        : EVENT_IDS.COMPLETED_ALT,
                    ) || "YES"
                  : "NO";
              case "expired":
                return hasEvent(data, EVENT_IDS.EXPIRED) ? "YES" : "NO";
              case "active":
                return getActiveTime(data);
              default:
                return params?.value ?? "";
            }
          },
        });

        return;
      }

      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allOrders: PrimeTradeData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const data: GetPrimeTradesListRequest = {
          tradeType: tradeType ? tradeType : undefined,
          tradeStatus: tradeStatus ? tradeStatus : undefined,
          page: i,
          rows: ROWS_PER_PAGE,
          primeType: countryFilter,
          isAutoSettle: isAutoSettle ? 1 : undefined,
        };

        const response: GetPrimeTradesListResponse =
          await primeServices.getPrimeTradesList(data);

        allOrders = [...allOrders, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allOrders.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allOrders = allOrders.slice(0, MAX_ROWS);

      const headers = [
        "Date",
        "Trade ID",
        "Trade Type",
        "Merchant",
        "Cashier",
        "UPI ID",
        "Fiat Amount",
        "Crypto Amount",
        "Status",
      ];

      let csvContent = headers.join(",") + "\n";

      allOrders.forEach((order) => {
        let status = "Pending";
        if (order.eventDetails && order.eventDetails.length > 0) {
          const sortedEvents = [...order.eventDetails].sort((a, b) => {
            return (
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
          });

          const latestEvent = sortedEvents[0];
          switch (latestEvent.eventId) {
            case "1":
              status = "Received";
              break;
            case "2":
              status = "Accepted";
              break;
            case "3":
              status = "Confirmed";
              break;
            case "4":
            case "6":
              status = "Completed";
              break;
            case "5":
              status = "Disputed";
              break;
            case "9":
              status = "Expired";
              break;
            default:
              status = "Pending";
          }
        }

        const row = [
          `"${constantFunctions.dateFormatInDDMMYY(order.createdAt)}"`,
          `"${order.tradeId}"`,
          `"${order.tradeType === "B" ? "Buy" : "Sell"}"`,
          `"${(order.merchantName || "").replace(/"/g, '""')}"`,
          `"${(order.cashierUsername || "").replace(/"/g, '""')}"`,
          `"${order.upiId || ""}"`,
          `"${order.fiatAmount.toFixed(2)}"`,
          `"${order.cryptoAmount.toFixed(2)}"`,
          `"${status}"`,
        ];

        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      link.setAttribute("download", `prime-orders-${formattedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export data", error);
      setError("Failed to export transaction data");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExpireTrade = async (tradeId: number) => {
    if (expiringTradeIds.has(tradeId)) return;

    setExpiringTradeIds((prev) => new Set(prev).add(tradeId));

    try {
      await primeServices.expireTrade(tradeId);

      // ✅ LOCAL STATE UPDATE → mark as expired
      setOrders((prev) =>
        prev.map((order) =>
          order.tradeId === tradeId
            ? {
                ...order,
                eventDetails: [
                  {
                    eventId: "9", // Expired
                    timestamp: new Date().toISOString(),
                  },
                ],
              }
            : order,
        ),
      );
      handleSuccess({ message: `Trade ${tradeId} expired successfully.` });
    } catch (error: any) {
      console.error("Expire trade failed", error);
      handleError({ message: `${error.response.data.msg}` });
    } finally {
      setExpiringTradeIds((prev) => {
        const copy = new Set(prev);
        copy.delete(tradeId);
        return copy;
      });
    }
  };

  const confirmExpireTrade = (
    tradeId: number,
    reason: "EXPIRED" | "COMPLETED",
  ) => {
    setConfirmTradeId(tradeId);
    setConfirmReason(reason);
  };

  const handleConfirmExpire = async () => {
    if (!confirmTradeId) return;
    await handleExpireTrade(confirmTradeId);
    setConfirmTradeId(null);
  };

  const handleCancelExpire = () => {
    setConfirmTradeId(null);
  };

  const columnDefs = useMemo(
    () =>
      createPrimeOrderColumnDefs(
        countryFilter,
        tradeType,
        handleExpireTrade,
        confirmExpireTrade,
        expiringTradeIds,
      ),
    [countryFilter, tradeType, expiringTradeIds],
  );

  return (
    <div>
      <div
        className={`${confirmTradeId ? "pointer-events-none opacity-40" : ""}`}
      >
        <div className={`flex flex-col gap-[12px] }`}>
          <div className="flex flex-row items-center justify-between pt-[20px]">
            <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden">
              <div className="flex justify-start space-x-3 items-center w-full">
                <p className="text-text-base text-[10px] font-bold whitespace-nowrap">
                  Filter by:
                </p>
                {countryFilter === "BR" && (
                  <>
                    <div className="flex space-x-3">
                      {["Buy", "Sell"].map((type) => (
                        <div
                          key={type}
                          onClick={
                            isLoading
                              ? undefined
                              : () =>
                                  setTradeType((prev) =>
                                    prev?.includes(type)
                                      ? prev.filter((s) => s !== type)
                                      : [...(prev || []), type],
                                  )
                          }
                          className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                            isLoading
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          } ${
                            tradeType?.includes(type)
                              ? "border-border-button-secondary bg-bg-button-secondary"
                              : "border-border-subtle bg-bg-page-body"
                          }`}
                        >
                          {type}
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        height: "20px",
                        width: "2px",
                        backgroundColor: "#ccc",
                      }}
                    ></div>
                  </>
                )}
                <div className="flex space-x-3">
                  {[
                    "Received",
                    "Accepted",
                    "Confirmed",
                    "Completed",
                    ...(countryFilter !== "BR" ? ["Disputed"] : []),
                    "Expired",
                  ].map((status) => (
                    <div
                      key={status}
                      onClick={
                        isLoading
                          ? undefined
                          : () =>
                              setTradeStatus((prev) =>
                                prev?.includes(status)
                                  ? prev.filter((s) => s !== status)
                                  : [...(prev || []), status],
                              )
                      }
                      className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                        isLoading
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      } ${
                        tradeStatus?.includes(status)
                          ? "border-border-button-secondary bg-bg-button-secondary"
                          : "border-border-subtle bg-bg-page-body"
                      }`}
                    >
                      {status}
                    </div>
                  ))}
                </div>

                {countryFilter === "BR" && (
                  <>
                    <div
                      style={{
                        height: "20px",
                        width: "2px",
                        backgroundColor: "#ccc",
                      }}
                    ></div>
                    <div className="flex space-x-3">
                      <div
                        onClick={
                          isLoading
                            ? undefined
                            : () => setIsAutoSettle(!isAutoSettle)
                        }
                        className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                          isLoading
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        } ${
                          isAutoSettle
                            ? "border-border-button-secondary bg-bg-button-secondary"
                            : "border-border-subtle bg-bg-page-body"
                        }`}
                      >
                        Auto
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden px-[12px] gap-4">
              <div className="flex-shrink-0">
                <LightCustomButton
                  text="EXPORT CSV"
                  isActive={true}
                  onClick={exportToCsv}
                  isButtonLoading={exportLoading}
                  className="whitespace-nowrap"
                  textSize="12px"
                />
              </div>
              <div className="w-[360px] flex-shrink-0">
                <GlobalSearchInputField
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  onSearch={handleSearchSubmit}
                  onClear={clearSearch}
                  disableSearchButton={!searchTerm.trim()}
                  placeholder="Search Order id, Trade id, UTR"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-row items-start">
            <div
              className={`flex flex-col bg-bg-page-body py-[12px] pr-[12px] overflow-hidden ${
                tradeId && !isPanelCollapsed ? "w-3/4" : "w-full"
              }`}
              style={{ height: "calc(100vh - 200px)" }}
            >
              {isLoading && page == 1 ? (
                <AgGridSkeleton
                  columnDefs={columnDefs}
                  style={{ height: "100%", width: "100%" }}
                />
              ) : (
                <div className="flex flex-col h-full">
                  <AgGridReactTable
                    ref={gridRef}
                    rowData={orders}
                    columnDefs={columnDefs}
                    onRowClicked={onRowClicked}
                    onBodyScroll={onBodyScroll}
                    getRowId={(params) => params.data.tradeId.toString()}
                    getRowClass={(params) =>
                      getPrimeOrderAgRowClass("trade", params.data)
                    }
                    error={error}
                  />
                  {isLoading && orders.length > 0 && (
                    <div className="flex items-center justify-center py-4 border-t border-border-subtle">
                      <div className="flex items-center space-x-2 text-text-subtle">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                        <span className="text-sm">Loading more orders...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {tradeId && (
              <div
                className={`bg-bg-page-body border-l border-border-subtle transition-all duration-300 relative ${
                  isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
                }`}
                style={{ height: "calc(100dvh - 70px)" }}
              >
                <button
                  onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                  className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
                  style={{
                    left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                    width: "32px",
                    height: "60px",
                  }}
                  aria-label={
                    isPanelCollapsed ? "Expand panel" : "Collapse panel"
                  }
                >
                  <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                    {isPanelCollapsed ? "chevron_left" : "chevron_right"}
                  </span>
                </button>

                <div
                  className={`overflow-auto h-full transition-opacity duration-300 ${
                    isPanelCollapsed
                      ? "opacity-0 pointer-events-none"
                      : "opacity-100"
                  }`}
                >
                  <PrimeDetailsTabs
                    tradeId={tradeId}
                    countryFilter={countryFilter}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmExpireModal
        open={confirmTradeId !== null}
        tradeId={confirmTradeId ?? undefined}
        reason={confirmReason}
        onConfirm={handleConfirmExpire}
        onCancel={handleCancelExpire}
        loading={
          confirmTradeId !== null && expiringTradeIds.has(confirmTradeId)
        }
      />
    </div>
  );
};

export default PrimeOrderFlow;
